import { UTCDate } from '@date-fns/utc';

import type { PrimaryFilterConfig } from '../../components/PageConfigProvider/page-reducer';
import { RevelioPageId } from '../../components/PageConfigProvider/types';
import { customSkillDefinition } from '../definitions/customSkill';
import { postDateDefinition } from '../definitions/postDate';
import {
  type Filter,
  FilterId,
  FilterType,
  type FreeTextFilter,
  isMultiFilter,
  type ListFilter,
  type PrimarySelection,
  type SchoolFilter,
  type StaticListFilter,
  type TreeFilter,
} from '../types/filters';
import {
  enforceMaxSelections,
  getActiveSelections,
  updatePrimarySelections,
} from '../utils/primaryFilterUtils';
import { ROLE_DRILLDOWN_LEVEL_ORDER } from '../utils/treemapDrilldownLevels';

export type FilterModification = {
  type:
    | 'condensed_to_or'
    | 'removed_not_filter'
    | 'limited_primary_selections'
    | 'limited_filter_instances'
    | 'normalized_filter_granularity';
  filterName: string;
};

const MAX_SECONDARY_TREE_SELECTIONS = 12;

const COMPOSITION_PAGE_IDS = new Set<RevelioPageId>([
  RevelioPageId.COMPANY_COMPOSITION,
  RevelioPageId.GEOGRAPHY_COMPOSITION,
  RevelioPageId.ROLE_COMPOSITION,
  RevelioPageId.COMPANY_COMPOSITION_V2,
  RevelioPageId.GEOGRAPHY_COMPOSITION_V2,
  RevelioPageId.ROLE_COMPOSITION_V2,
]);

const CROSS_GRANULARITY_PAGE_IDS = new Set<RevelioPageId>([
  RevelioPageId.TALENT_DISCOVERY,
  RevelioPageId.COMPANY_SCREENER_V2,
  RevelioPageId.GEOGRAPHY_SCREENER_V2,
  RevelioPageId.ROLE_SCREENER_V2,
]);

const floorToMonthStart = (date: UTCDate | null | undefined): UTCDate | null =>
  date ? new UTCDate(date.getUTCFullYear(), date.getUTCMonth(), 1) : null;

// dash-meta resolves full-precision dates. Posting Discovery uses the exact day
// (mapped to the day-level POST_DATE filter). Other pages follow their date-axis
// granularity: week-granularity pages (e.g. Company > Job Postings) keep the
// exact day, monthly pages floor to the start of the month for their monthly
// analytics axis. Applied up front so it also covers the cross-granularity pages
// (Talent Discovery, screeners) that return early below.
const adaptDateRangeForPage = (
  filter: Filter,
  revelioPageId: RevelioPageId | undefined,
  viewGranularity: 'week' | 'month'
): Filter => {
  if (
    filter.id !== FilterId.DATE_RANGE ||
    filter.type !== FilterType.DATE_RANGE
  ) {
    return filter;
  }
  if (revelioPageId === RevelioPageId.POSTING_DISCOVERY) {
    return {
      ...postDateDefinition,
      min: filter.min,
      max: filter.max,
      // The AI range is already a precise inclusive day, so don't pad it out to
      // a week end the way a manual week-picker selection is padded.
      exactEnd: true,
      isUserSubmitted: true,
      isVisible: true,
    };
  }
  // Week-granularity pages render a day-level date axis, so keep the exact day.
  if (viewGranularity === 'week') {
    return filter;
  }
  return {
    ...filter,
    min: floorToMonthStart(filter.min),
    max: floorToMonthStart(filter.max),
  };
};

const isCompositionPage = (pageId: RevelioPageId): boolean =>
  COMPOSITION_PAGE_IDS.has(pageId);

const dedupeByKey = <T>(items: T[], getKey: (item: T) => string): T[] => {
  const unique = new Map<string, T>();
  items.forEach((item) => {
    const key = getKey(item);
    if (!unique.has(key)) {
      unique.set(key, item);
    }
  });
  return Array.from(unique.values());
};

const hasNotFlag = (filter: Filter): boolean =>
  'isNot' in filter && Boolean(filter.isNot);

type CondenseMultiFilterResult = {
  filter: Filter;
  didDropInstances: boolean;
};

const condenseMultiFilterToSingle = (
  multiFilter: Filter
): CondenseMultiFilterResult => {
  if (!isMultiFilter(multiFilter)) {
    return { filter: multiFilter, didDropInstances: false };
  }

  const firstInstance = multiFilter.instances[0];
  if (!firstInstance) {
    return { filter: multiFilter, didDropInstances: true };
  }

  // Combine selections into the first instance to preserve OR semantics.
  if (multiFilter.instances.length > 1) {
    if (firstInstance.type === FilterType.TREE) {
      const allSelections = multiFilter.instances.flatMap(
        (instance) => (instance as TreeFilter).selections
      );
      const selections = dedupeByKey(
        allSelections,
        (selection) => `${selection.levelId}:${selection.id}`
      );
      return {
        filter: {
          ...firstInstance,
          selections,
        } as Filter,
        didDropInstances: false,
      };
    }
    if (firstInstance.type === FilterType.LIST) {
      const allSelections = multiFilter.instances.flatMap(
        (instance) => (instance as ListFilter).selections
      );
      const selections = dedupeByKey(
        allSelections,
        (selection) => selection.id
      );
      return {
        filter: {
          ...firstInstance,
          selections,
        } as Filter,
        didDropInstances: false,
      };
    }
    if (firstInstance.type === FilterType.STATIC_LIST) {
      const allSelections = multiFilter.instances.flatMap(
        (instance) => (instance as StaticListFilter).selections
      );
      const selections = dedupeByKey(
        allSelections,
        (selection) => selection.id
      );
      return {
        filter: {
          ...firstInstance,
          selections,
        } as Filter,
        didDropInstances: false,
      };
    }
    if (firstInstance.type === FilterType.FREE_TEXT) {
      const allText = multiFilter.instances.flatMap(
        (instance) => (instance as FreeTextFilter).text
      );
      const text = Array.from(new Set(allText));
      return {
        filter: {
          ...firstInstance,
          text,
        } as Filter,
        didDropInstances: false,
      };
    }
    if (firstInstance.type === FilterType.SCHOOL) {
      // Combine all schoolResultItems from each instance.
      const allSchoolItems = multiFilter.instances.reduce(
        (acc, instance) => ({
          ...acc,
          ...(instance as SchoolFilter).schoolResultItems,
        }),
        {}
      );
      return {
        filter: {
          ...firstInstance,
          schoolResultItems: allSchoolItems,
        } as Filter,
        didDropInstances: false,
      };
    }
  }

  return {
    filter: firstInstance,
    didDropInstances: multiFilter.instances.length > 1,
  };
};

const getSelectionKey = (selection: TreeFilter['selections'][number]): string =>
  `${selection.levelId}:${selection.id}`;

const limitSecondaryTreeSelections = (
  filter: TreeFilter
): { filter: TreeFilter; wasLimited: boolean } => {
  if (filter.selections.length <= MAX_SECONDARY_TREE_SELECTIONS) {
    return { filter, wasLimited: false };
  }

  return {
    filter: {
      ...filter,
      selections: filter.selections.slice(0, MAX_SECONDARY_TREE_SELECTIONS),
    },
    wasLimited: true,
  };
};

// Per-filter level orders, coarsest → finest. Tie-break prefers coarser.
// levelId values match what AI search resolvers emit:
//  - GEOGRAPHY: union of msa- and administrative-mode levels (resolver emits
//    only the active mode's values per session, so the union doesn't tie-break
//    across modes in practice).
//  - CUSTOM_SKILL: same selectionListIds as customSkillDefinition.
//  - COMPANY: RICS and Company share FilterId.COMPANY; selections differ by
//    levelId. Order mirrors treemapDrilldownLevels.ts.
const FILTER_GRANULARITY_LEVEL_ORDER: Partial<
  Record<FilterId, readonly string[]>
> = {
  [FilterId.ROLE]: ROLE_DRILLDOWN_LEVEL_ORDER,
  [FilterId.GEOGRAPHY]: ['region', 'country', 'msa', 'state', 'city'],
  [FilterId.CUSTOM_SKILL]: customSkillDefinition.selectionListIds,
  [FilterId.COMPANY]: ['rics_k10', 'rics_k50', 'rics_k400', 'company'],
};

const normalizeFilterGranularity = (
  filter: TreeFilter,
  levelOrder: readonly string[]
): { filter: TreeFilter; wasNormalized: boolean } => {
  const selectionsByLevel = new Map<string, TreeFilter['selections']>();
  for (const selection of filter.selections) {
    const existing = selectionsByLevel.get(selection.levelId) ?? [];
    selectionsByLevel.set(selection.levelId, [...existing, selection]);
  }

  if (selectionsByLevel.size <= 1) {
    return { filter, wasNormalized: false };
  }

  let bestLevelId = '';
  let bestCount = 0;
  for (const [levelId, selections] of selectionsByLevel) {
    const count = selections.length;
    const isBetter =
      count > bestCount ||
      (count === bestCount &&
        levelOrder.indexOf(levelId) < levelOrder.indexOf(bestLevelId));
    if (isBetter) {
      bestLevelId = levelId;
      bestCount = count;
    }
  }

  return {
    filter: {
      ...filter,
      selections: selectionsByLevel.get(bestLevelId) ?? [],
    },
    wasNormalized: true,
  };
};

const applySelectionLimit = (
  filter: Filter
): { filter: Filter; wasLimited: boolean } => {
  if (filter.isPrimary) return { filter, wasLimited: false };
  if (filter.type !== FilterType.TREE) return { filter, wasLimited: false };
  const { filter: limitedFilter, wasLimited } =
    limitSecondaryTreeSelections(filter);
  return { filter: limitedFilter, wasLimited };
};

const isPrimaryFilterCandidate = (
  filter: Filter,
  primaryFilterId: PrimaryFilterConfig['id']
): boolean => {
  if (filter.id === primaryFilterId) return true;
  if (primaryFilterId === FilterId.COMPANY && filter.id === FilterId.INDUSTRY) {
    return true;
  }
  return (
    filter.type === FilterType.MULTI &&
    isMultiFilter(filter) &&
    filter.wrappedFilterId === primaryFilterId
  );
};

const getPrimarySelectionsFromFilter = (
  filter: Filter
): TreeFilter['selections'] => {
  if (filter.type === FilterType.TREE) {
    return hasNotFlag(filter) ? [] : filter.selections;
  }

  if (filter.type === FilterType.MULTI && isMultiFilter(filter)) {
    const instances = filter.instances.filter(
      (instance): instance is TreeFilter =>
        instance.type === FilterType.TREE && !hasNotFlag(instance)
    );
    const selections = instances.flatMap((instance) => instance.selections);
    return dedupeByKey(selections, getSelectionKey);
  }

  return [];
};

const getCurrentPrimarySelections = (
  filter: Filter | undefined
): PrimarySelection[] => {
  if (!filter || filter.type !== FilterType.TREE) {
    return [];
  }

  if (filter.primarySelections && filter.primarySelections.length > 0) {
    return filter.primarySelections;
  }

  return filter.selections.map((selection) => ({
    ...selection,
    isActive: true,
  }));
};

const toTreeSelections = (
  selections: PrimarySelection[]
): TreeFilter['selections'] =>
  selections.map(({ isActive: _isActive, ...rest }) => rest);

const getPrimaryBaseFilter = (filter: Filter): TreeFilter | null => {
  if (filter.type === FilterType.TREE) {
    return filter;
  }
  if (filter.type === FilterType.MULTI && isMultiFilter(filter)) {
    const condensed = condenseMultiFilterToSingle(filter);
    return condensed.filter.type === FilterType.TREE
      ? (condensed.filter as TreeFilter)
      : null;
  }
  return null;
};

export const adaptAiSearchFiltersForPage = ({
  filters,
  revelioPageId,
  primaryFilterConfig,
  currentPrimaryFilter,
  primarySelectionMergeMode = 'merge',
  viewGranularity = 'month',
}: {
  filters: Filter[];
  revelioPageId: RevelioPageId | undefined;
  primaryFilterConfig: PrimaryFilterConfig | undefined;
  currentPrimaryFilter: Filter | undefined;
  /** `merge`: combine AI primary picks with existing primary (default). `replace`: wipe page defaults. */
  primarySelectionMergeMode?: 'merge' | 'replace';
  /** Page date-axis granularity; week keeps the exact day, month floors to month-start. */
  viewGranularity?: 'week' | 'month';
}): { adaptedFilters: Filter[]; modifications: FilterModification[] } => {
  // Normalise the AI date range to each page's granularity up front so it applies
  // on every path, including the cross-granularity pages that return early below.
  const workingFilters = filters.map((filter) =>
    adaptDateRangeForPage(filter, revelioPageId, viewGranularity)
  );

  if (
    revelioPageId !== undefined &&
    CROSS_GRANULARITY_PAGE_IDS.has(revelioPageId)
  ) {
    type TdResult = {
      filter: Filter | null;
      modification?: FilterModification;
    };
    const processTdFilter = (filter: Filter): TdResult => {
      if (
        primaryFilterConfig &&
        isPrimaryFilterCandidate(filter, primaryFilterConfig.id)
      ) {
        if (filter.type === FilterType.TREE && !hasNotFlag(filter)) {
          return { filter: { ...filter, isPrimary: true } };
        }
        if (
          filter.type === FilterType.MULTI &&
          isMultiFilter(filter) &&
          filter.wrappedFilterId === primaryFilterConfig.id
        ) {
          return {
            filter: {
              ...filter,
              instances: filter.instances.map((inst) =>
                inst.type === FilterType.TREE && !hasNotFlag(inst)
                  ? { ...inst, isPrimary: true }
                  : inst
              ),
            } as Filter,
          };
        }
        return { filter };
      }
      if (filter.type === FilterType.MULTI && isMultiFilter(filter)) {
        if (filter.instances.length === 0) return { filter: null };

        // On TD, preserve multiple instances (AND semantics) — only apply
        // per-instance selection limits instead of condensing to a single chip.
        let wasLimited = false;
        const limitedInstances = filter.instances.map((instance) => {
          const { filter: limited, wasLimited: instanceLimited } =
            applySelectionLimit(instance);
          if (instanceLimited) wasLimited = true;
          return limited;
        });

        return {
          filter: { ...filter, instances: limitedInstances } as Filter,
          modification: wasLimited
            ? {
                type: 'limited_primary_selections',
                filterName: filter.name,
              }
            : undefined,
        };
      }
      if (filter.type === FilterType.TREE) {
        const limited = applySelectionLimit(filter);
        return {
          filter: limited.filter,
          modification: limited.wasLimited
            ? {
                type: 'limited_primary_selections',
                filterName: limited.filter.name,
              }
            : undefined,
        };
      }
      return { filter };
    };

    const modificationKey = (m: FilterModification) =>
      `${m.type}:${m.filterName}`;
    const results = workingFilters.map(processTdFilter);
    const adaptedFilters = results.flatMap((r) =>
      r.filter !== null ? [r.filter] : []
    );
    const modifications = (() => {
      const mods = results.flatMap((r) =>
        r.modification !== undefined ? [r.modification] : []
      );
      const seen = new Set<string>();
      return mods.filter((m) => {
        const key = modificationKey(m);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    })();

    return { adaptedFilters, modifications };
  }

  const modifications: FilterModification[] = [];
  const modificationKeys = new Set<string>();
  const addModification = (modification: FilterModification) => {
    const key = `${modification.type}:${modification.filterName}`;
    if (modificationKeys.has(key)) return;
    modificationKeys.add(key);
    modifications.push(modification);
  };

  const adaptedFilters: Filter[] = [];
  // Posting Discovery's DATE_RANGE -> POST_DATE mapping is done up front by
  // adaptDateRangeForPage (applied to workingFilters above), so no per-page date
  // handling is needed here.
  let remainingFilters = [...workingFilters];

  // Primary filter requires special handling (limits, preserving selections).
  if (primaryFilterConfig) {
    const primaryFilterId = primaryFilterConfig.id;
    const selectionLimit = primaryFilterConfig.selectionLimit ?? 1;
    const compositionPage =
      revelioPageId !== undefined && isCompositionPage(revelioPageId);

    const primaryCandidates = remainingFilters.filter((filter) =>
      isPrimaryFilterCandidate(filter, primaryFilterId)
    );

    if (primaryCandidates.length > 0) {
      remainingFilters = remainingFilters.filter(
        (filter) => !isPrimaryFilterCandidate(filter, primaryFilterId)
      );
    }

    const aiPrimarySelections = dedupeByKey(
      primaryCandidates.flatMap(getPrimarySelectionsFromFilter),
      getSelectionKey
    );

    if (aiPrimarySelections.length === 0) {
      // AI did not return a primary filter; preserve current selection.
      if (currentPrimaryFilter) {
        adaptedFilters.push(currentPrimaryFilter);
      }
    } else {
      const currentPrimarySelections = getCurrentPrimarySelections(
        primarySelectionMergeMode === 'replace' &&
          aiPrimarySelections.length > 0
          ? undefined
          : currentPrimaryFilter
      );
      const mergedSelections = dedupeByKey(
        [...toTreeSelections(currentPrimarySelections), ...aiPrimarySelections],
        getSelectionKey
      );

      const primarySelections = (() => {
        if (!compositionPage) {
          return updatePrimarySelections(
            mergedSelections,
            currentPrimarySelections,
            selectionLimit
          );
        }

        const aiSelectionKeys = new Set(
          aiPrimarySelections.map(getSelectionKey)
        );
        const orderedSelections = dedupeByKey(
          [
            ...aiPrimarySelections,
            ...toTreeSelections(currentPrimarySelections),
          ],
          getSelectionKey
        );
        const nextSelections: PrimarySelection[] = orderedSelections.map(
          (selection) => ({
            ...selection,
            isActive: aiSelectionKeys.has(getSelectionKey(selection)),
          })
        );

        return enforceMaxSelections(nextSelections, selectionLimit);
      })();

      const activePrimaryKeys = new Set(
        getActiveSelections(primarySelections).map(getSelectionKey)
      );
      const aiSelectionsLimited = aiPrimarySelections.some(
        (selection) => !activePrimaryKeys.has(getSelectionKey(selection))
      );

      const primaryBaseFromAi = primaryCandidates
        .map(getPrimaryBaseFilter)
        .find((filter): filter is TreeFilter => filter !== null);
      const currentPrimaryBase =
        currentPrimaryFilter && currentPrimaryFilter.type === FilterType.TREE
          ? currentPrimaryFilter
          : null;
      const baseFilter = currentPrimaryBase ?? primaryBaseFromAi;

      if (baseFilter) {
        const primaryFilterToAdd = {
          ...baseFilter,
          id: primaryFilterId,
          selections: getActiveSelections(primarySelections),
          isPrimary: true,
          primarySelections,
        };

        adaptedFilters.push(primaryFilterToAdd);
        if (aiSelectionsLimited) {
          addModification({
            type: 'limited_primary_selections',
            filterName: baseFilter.name,
          });
        }
      }
    }
  }

  for (const filter of remainingFilters) {
    let nextFilter: Filter | null = filter;

    // Remove NOT instances from multi-filters (NOT not supported on non-TD pages).
    if (
      nextFilter &&
      nextFilter.type === FilterType.MULTI &&
      isMultiFilter(nextFilter)
    ) {
      if (nextFilter.instances.length === 0) {
        continue;
      }
      const filteredInstances = nextFilter.instances.filter(
        (instance) => !hasNotFlag(instance)
      );
      if (filteredInstances.length !== nextFilter.instances.length) {
        addModification({
          type: 'removed_not_filter',
          filterName: nextFilter.name,
        });
      }
      if (filteredInstances.length === 0) {
        continue;
      }
      if (filteredInstances.length !== nextFilter.instances.length) {
        nextFilter = {
          ...nextFilter,
          instances: filteredInstances,
        } as Filter;
      }
    }

    // Remove filters with isNot=true (NOT not supported on non-TD pages).
    if (nextFilter && 'isNot' in nextFilter && nextFilter.isNot) {
      addModification({
        type: 'removed_not_filter',
        filterName: nextFilter.name,
      });
      continue;
    }

    // Condense multi-filter instances (AND -> OR) to a single filter.
    if (nextFilter && nextFilter.type === FilterType.MULTI) {
      if (!isMultiFilter(nextFilter)) {
        continue;
      }
      if (nextFilter.instances.length > 1) {
        const condensedFilterResult = condenseMultiFilterToSingle(nextFilter);
        const baseFilterResult = applySelectionLimit({
          ...condensedFilterResult.filter,
          id: nextFilter.wrappedFilterId,
        });
        adaptedFilters.push(baseFilterResult.filter);
        if (condensedFilterResult.didDropInstances) {
          addModification({
            type: 'limited_filter_instances',
            filterName: nextFilter.name,
          });
        } else {
          addModification({
            type: 'condensed_to_or',
            filterName: nextFilter.name,
          });
        }
        if (baseFilterResult.wasLimited) {
          addModification({
            type: 'limited_primary_selections',
            filterName: baseFilterResult.filter.name,
          });
        }
        continue;
      }
      if (nextFilter.instances.length === 1) {
        const unwrappedInstance = nextFilter.instances[0];
        if (!unwrappedInstance) {
          continue;
        }
        const unwrappedFilterResult = applySelectionLimit({
          ...unwrappedInstance,
          id: nextFilter.wrappedFilterId,
        });
        adaptedFilters.push(unwrappedFilterResult.filter);
        if (unwrappedFilterResult.wasLimited) {
          addModification({
            type: 'limited_primary_selections',
            filterName: unwrappedFilterResult.filter.name,
          });
        }
        continue;
      }
      continue;
    }

    if (nextFilter) {
      const limitedFilterResult = applySelectionLimit(nextFilter);
      adaptedFilters.push(limitedFilterResult.filter);
      if (limitedFilterResult.wasLimited) {
        addModification({
          type: 'limited_primary_selections',
          filterName: limitedFilterResult.filter.name,
        });
      }
    }
  }

  const normalizedFilters = adaptedFilters.map((filter) => {
    if (filter.isPrimary || filter.type !== FilterType.TREE) {
      return filter;
    }
    const levelOrder = FILTER_GRANULARITY_LEVEL_ORDER[filter.id];
    if (!levelOrder) {
      return filter;
    }
    const { filter: normalized, wasNormalized } = normalizeFilterGranularity(
      filter,
      levelOrder
    );
    if (wasNormalized) {
      addModification({
        type: 'normalized_filter_granularity',
        filterName: filter.name,
      });
    }
    return normalized;
  });

  return { adaptedFilters: normalizedFilters, modifications };
};
